/**
 * FocusModeContext — macOS-style Do Not Disturb / Focus Mode
 * localStorage key: runai_focus_mode
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react'

export type FocusMode = 'work' | 'sleep' | 'personal' | 'custom'

interface FocusModeState {
  enabled: boolean
  mode: FocusMode
}

interface FocusModeContextValue extends FocusModeState {
  toggle: () => void
  setMode: (m: FocusMode) => void
}

const KEY = 'runai_focus_mode'

function load(): FocusModeState {
  try {
    const raw = localStorage.getItem(KEY)
    if (raw) return JSON.parse(raw)
  } catch { /* ignore */ }
  return { enabled: false, mode: 'work' }
}

const FocusModeContext = createContext<FocusModeContextValue | null>(null)

export function FocusModeProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<FocusModeState>(load)

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(state))
  }, [state])

  const toggle = useCallback(() => setState(s => ({ ...s, enabled: !s.enabled })), [])
  const setMode = useCallback((mode: FocusMode) => setState(s => ({ ...s, mode })), [])

  return (
    <FocusModeContext.Provider value={{ ...state, toggle, setMode }}>
      {children}
    </FocusModeContext.Provider>
  )
}

export function useFocusMode() {
  const ctx = useContext(FocusModeContext)
  if (!ctx) throw new Error('useFocusMode must be inside FocusModeProvider')
  return ctx
}
